import type { Dispatch, SetStateAction } from 'react';
import type { Attachment, AttachmentInput } from 'sr-types/lib/production/v1/graphql';
import { AllPermissions } from '../auth/permissions';
import type { LinkState } from '../components/types';
import {
    ATTACHMENT_ERRORS,
    DeleteAttachmentMutationError,
    DeleteAttachmentServerError,
    SaveAttachmentMutationError,
    SaveAttachmentServerError
} from './constants';

const MAX_FILE_SIZE_MB = 20;
export const defaultMaxFileSize = MAX_FILE_SIZE_MB * 1024 * 1024;

export const normalizeTags = (attachment: AttachmentInput | Attachment) => {
    return attachment.tags || attachment.tagSet || [];
};

export const createAttachmentInput = (file: Attachment | AttachmentInput): AttachmentInput => {
    return {
        entityReference: file.entityReference,
        name: file.name,
        type: file.type,
        size: file.size,
        tags: normalizeTags(file)
    };
};

export const filteredAttachments = (input: Attachment[] | AttachmentInput[]) => input.map(createAttachmentInput);

export type SaveAttachmentsResponse = {
    data?: {
        saveAttachments?: Array<Pick<Attachment, 'id'>>;
    };
};

export type AttachmentInputWithOptionalId = AttachmentInput & {
    id?: string;
    security?: Attachment['security'];
};

const getSavedAttachments = (response: SaveAttachmentsResponse | void): Array<Pick<Attachment, 'id'>> => {
    if (!response) {
        return [];
    }

    return response.data?.saveAttachments || [];
};

const getSavedAttachmentId = (
    savedAttachments: Array<Pick<Attachment, 'id'>>,
    index: number,
    startIndex: number,
    attachmentCount: number
): string | undefined => {
    if (savedAttachments.length >= startIndex + attachmentCount) {
        return savedAttachments[startIndex + index]?.id;
    }

    if (savedAttachments.length === attachmentCount) {
        return savedAttachments[index]?.id;
    }

    return undefined;
};

export const applySavedAttachmentIds = (
    attachments: AttachmentInputWithOptionalId[],
    response: SaveAttachmentsResponse | void,
    startIndex = 0
): AttachmentInputWithOptionalId[] => {
    const savedAttachments = getSavedAttachments(response);
    const attachmentsWithIds = attachments.map((attachment, index) => {
        // saveAttachments returns an array; only map ids when the response aligns with the saved inputs.
        const savedAttachmentId = getSavedAttachmentId(savedAttachments, index, startIndex, attachments.length);
        const attachmentWithId: AttachmentInputWithOptionalId = {
            ...attachment,
            ...(!attachment.id && !attachment.security
                ? {
                      security: {
                          allowedActions: [
                              AllPermissions.Read,
                              AllPermissions.Edit,
                              AllPermissions.Delete,
                              AllPermissions.Share
                          ]
                      }
                  }
                : {}),
            ...(savedAttachmentId ? { id: savedAttachmentId } : {})
        };

        return attachmentWithId;
    });

    return attachmentsWithIds;
};

export const hasAttachmentId = (
    attachment: AttachmentInputWithOptionalId
): attachment is AttachmentInputWithOptionalId & { id: string } => {
    return Boolean(attachment.id);
};

type AttachmentMergeKey = string | AttachmentInput;

const getAttachmentMergeKey = (attachment: AttachmentInput): AttachmentMergeKey => {
    return attachment.entityReference?.id || (attachment as Attachment).id || attachment;
};

export const mergeAttachmentsByReference = (attachments: AttachmentInput[]): AttachmentInput[] => {
    const attachmentsByReference = new Map<AttachmentMergeKey, AttachmentInput>();

    attachments.forEach((attachment) => {
        const mergeKey = getAttachmentMergeKey(attachment);
        const existingAttachment = attachmentsByReference.get(mergeKey);
        // Replace temporary upload/link rows with the saved row once saveAttachments returns the id.
        if (!existingAttachment || (!(existingAttachment as Attachment).id && (attachment as Attachment).id)) {
            attachmentsByReference.set(mergeKey, attachment);
        }
    });

    return Array.from(attachmentsByReference.values());
};

export const removeAttachmentByReferenceId = (
    attachments: AttachmentInput[],
    referenceId?: string
): AttachmentInput[] => attachments.filter((attachment) => attachment.entityReference?.id !== referenceId);

export const applyOptimisticAttachments = (
    setAttachments: Dispatch<SetStateAction<AttachmentInput[]>>,
    previousAttachments: AttachmentInput[],
    nextAttachments: AttachmentInput[]
) => {
    setAttachments(nextAttachments);
    return () => setAttachments(previousAttachments);
};

export const isNewAttachment = (attachment: AttachmentInput): boolean =>
    !(attachment as Attachment).id && Boolean(attachment.entityReference?.id);

const haveSameTags = (left: AttachmentInput, right: AttachmentInput): boolean => {
    const leftTags = normalizeTags(left);
    const rightTags = normalizeTags(right);
    return leftTags.length === rightTags.length && leftTags.every((tag, index) => tag === rightTags[index]);
};

export const buildOptimisticSaveAttachments = (
    currentAttachments: AttachmentInput[],
    attachmentsToSave: AttachmentInput[]
): AttachmentInput[] => {
    const optimisticCreates = applySavedAttachmentIds(attachmentsToSave.filter(isNewAttachment), undefined);
    const editedByReferenceId = new Map(
        attachmentsToSave
            .filter((attachment) => !isNewAttachment(attachment) && attachment.entityReference?.id)
            .flatMap((attachment) => {
                const referenceId = attachment.entityReference?.id;
                const existing = currentAttachments.find((item) => item.entityReference?.id === referenceId);
                if (!referenceId || !existing || haveSameTags(existing, attachment)) {
                    return [];
                }
                return [[referenceId, attachment] as const];
            })
    );
    const withEdits = currentAttachments.map((attachment) => {
        const referenceId = attachment.entityReference?.id;
        const edited = referenceId ? editedByReferenceId.get(referenceId) : undefined;
        return edited ? { ...attachment, ...edited } : attachment;
    });

    return mergeAttachmentsByReference([...optimisticCreates, ...withEdits]);
};

export const mergeSavedAttachments = (
    currentAttachments: AttachmentInput[],
    savedAttachments: AttachmentInput[]
): AttachmentInput[] => {
    const savedByReferenceId = new Map(
        savedAttachments.flatMap((attachment) => {
            const referenceId = attachment.entityReference?.id;
            return referenceId ? [[referenceId, attachment] as const] : [];
        })
    );

    return currentAttachments.map((attachment) => {
        const referenceId = attachment.entityReference?.id;
        return referenceId && savedByReferenceId.has(referenceId)
            ? (savedByReferenceId.get(referenceId) as AttachmentInput)
            : attachment;
    });
};

export const EMPTY_LINK: LinkState = {
    isLink: true,
    linkURL: '',
    linkName: '',
    tags: []
};

const GENERIC_DELETE_ERROR_MESSAGES: string[] = [
    ATTACHMENT_ERRORS.deleteMutationErrorMessage,
    ATTACHMENT_ERRORS.deleteServerErrorMessage
];

// Server rejections (e.g. "attachments cannot be deleted from sent Order") are actionable, so show them verbatim.
export const getAttachmentDeletionFailureReason = (error: unknown): string | undefined => {
    if (!(error instanceof DeleteAttachmentMutationError || error instanceof DeleteAttachmentServerError)) {
        return undefined;
    }
    return GENERIC_DELETE_ERROR_MESSAGES.includes(error.message) ? undefined : error.message;
};

const GENERIC_SAVE_ERROR_MESSAGES: string[] = [
    ATTACHMENT_ERRORS.saveMutationErrorMessage,
    ATTACHMENT_ERRORS.saveServerErrorMessage
];

export const getAttachmentSaveFailureReason = (error: unknown): string | undefined => {
    if (!(error instanceof SaveAttachmentMutationError || error instanceof SaveAttachmentServerError)) {
        return undefined;
    }
    return GENERIC_SAVE_ERROR_MESSAGES.includes(error.message) ? undefined : error.message;
};
